import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import {
  StyledHeader,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import { toFriendlyDateString } from 'helpers/Formatter';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import { AccentColor } from 'helpers/AccentColorHelper';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import AntDrawer from 'components/ui/Drawer';
import MediaQuery from 'react-responsive';
import StatusTag from 'components/ui/StatusTag';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { colors } from 'helpers/ColorHelper';
import { getFilteredBatchPayments, getBatch, clearBatchPayments } from '../../store/actions';
import { clearResidentsWithPAPDetails } from 'actions/landlordPropertiesActions';
import BatchPaymentCreating from './LinkedPayments/BatchPaymentCreating';
import BatchPaymentEditing from './LinkedPayments/BatchPaymentEditing';
import BatchPaymentDeleting from './BatchPaymentDeleting';

const ScrollablePanel = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
`;

const StyledButton = styled(StyledAddButton)`
  background-color: ${colors.LIGHT_BLUE_GREY};
  color: ${colors.ULTRAMARINE_BLUE};
  text-transform: none;
  margin: 0;
`;

class LinkedPayments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      searchActive: false,
      currentTab: 'draft',
      sortKey: '',
      sortOrder: '',
      offset: 0,
      createBatchPaymentDrawerOpen: false,
      editBatchPaymentDrawerOpen: false,
      deleteBatchPaymentDrawerOpen: false,
      batchPaymentId: null,
      batchPayment: {},
    };
  }

  componentDidMount() {
    this.fetchFilteredPayments();
  }

  componentWillUnmount() {
    this.props.clearBatchPayments();
  }

  handleTabChange = tabName => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleClearFilters = () => {
    this.setState(
      {
        search: '',
        searchActive: false,
        currentTab: 'draft',
        sortKey: '',
        sortOrder: '',
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleSubmitFilters = values => {
    this.setState(
      {
        search: values.search,
        searchActive: values.search !== '',
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  fetchFilteredPayments = async () => {
    const { search, searchActive, currentTab, offset, sortKey, sortOrder } = this.state;
    let filterOptions = { sort_key: sortKey, sort_order: sortOrder };
    let searchWithinTab = currentTab;
    if (searchActive) {
      filterOptions.query = search;
      searchWithinTab = 'all';
    }

    const batchId = this.props.batch.id;

    await this.props.getFilteredBatchPayments(batchId, searchWithinTab, offset, filterOptions);
    this.setState({ offset: this.props.batchPayments.length });
  };

  reloadTable = () => {
    this.setState(
      {
        offset: 0,
      },
      () => this.fetchFilteredPayments()
    );
  };

  openCreateBatchDrawer = () => {
    // when selecting a unit we fetch residents and are storing them in the properties store
    // clear the store so we don't have previous residents
    this.props.clearResidentsWithPAPDetails([]);
    this.setState({
      createBatchPaymentDrawerOpen: true,
    });
  };

  closeCreateBatchDrawer = () => {
    this.setState({
      createBatchPaymentDrawerOpen: false,
    });
  };

  openEditDrawer = data => {
    const formData = {
      unit_id: data.unit_id,
      cotenant_id: data.cotenant_id,
      amount: data.amount,
      description: data.description,
      payment_method: data.payment_method,
    };
    this.setState({
      batchPayment: formData,
      batchPaymentId: data.original_id || data.id,
      editBatchPaymentDrawerOpen: true,
    });
  };

  closeEditDrawer = () => {
    this.setState({
      batchPayment: {},
      batchPaymentId: null,
      editBatchPaymentDrawerOpen: false,
    });
  };

  openDeleteBatchPaymentDrawer = batchPaymentId => {
    this.setState({
      deleteBatchPaymentDrawerOpen: true,
      batchPaymentId,
    });
  };

  closeDeleteBatchPaymentDrawer = () => {
    this.setState({
      deleteBatchPaymentDrawerOpen: false,
    });
  };

  closeDeleteDialog = () => {
    this.setState({
      editBatchPaymentDrawerOpen: false,
    });
  };

  render() {
    const columns = [
      {
        title: getTranslatedString('landlordPortal.batches.status'),
        dataKey: 'status',
        sortable: false,
        render: ({ data }) => <StatusTag status={data} />,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.resident'),
        dataKey: 'cotenant_full_name',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.description'),
        dataKey: 'description',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.batchPaymentId'),
        dataKey: 'original_id',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.unit'),
        dataKey: 'unit_number',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.amount'),
        dataKey: 'amount',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.processedDate'),
        dataKey: 'processed_at',
        sortable: true,
        width: 300,
        render: ({ data }) => toFriendlyDateString(data) || '-',
      },
    ];

    return (
      <ScrollablePanel>
        <StyledHeaderContainer>
          <StyledHeader>
            <Form.Item>
              {this.props.batch.status === 'draft' && (
                <StyledButton accentColor={AccentColor()} onClick={this.openCreateBatchDrawer}>
                  {getTranslatedString('landlordPortal.batches.addNewPayment')}
                </StyledButton>
              )}
              {this.props.batch.status !== 'draft' &&
                getTranslatedString('landlordPortal.batches.addNewPaymentHidden')}
            </Form.Item>
          </StyledHeader>
          <TableFilters
            filters={{ search: this.state.search }}
            handleSubmitFilters={this.handleSubmitFilters}
            handleClearFilters={this.handleClearFilters}
            filterOptions={[]}
            accentColor={AccentColor()}
            bgColor="white"
          />
        </StyledHeaderContainer>
        <Row gutter={16}>
          <Col xs={24}>
            <Spacer mt={0}>
              <StyledTableContainer>
                <MediaQuery query="(min-device-width: 750px)">
                  <TabbedTable
                    setRef={ref => {
                      this.table = ref;
                    }}
                    accentColor={AccentColor()}
                    tabs={[]}
                    columns={columns}
                    data={this.props.batchPayments}
                    loadMoreData={this.fetchFilteredPayments}
                    filtersActive={this.state.filtersActive}
                    onRowClick={data => this.openEditDrawer(data)}
                    onColumnClick={data => this.handleSort(data.key, data.order)}
                    searchTotalResultsCount={this.props.searchResultCount}
                  />
                </MediaQuery>
                <MediaQuery query="(max-device-width: 750px)">
                  <MobileTable
                    data={this.props.batchPayments}
                    columns={columns}
                    tag="status"
                    stickyHeaderTop={'107px'}
                    rowHeader="cotenant_full_name"
                    fieldsAboveFold={[
                      {
                        title: getTranslatedString('landlordPortal.batches.unit'),
                        key: 'unit_number',
                      },
                      {
                        title: getTranslatedString('landlordPortal.batches.amount'),
                        key: 'amount',
                      },
                    ]}
                    fieldsBelowFold={[
                      {
                        title: getTranslatedString('landlordPortal.batches.description'),
                        key: 'description',
                      },
                      {
                        title: getTranslatedString('landlordPortal.batches.processedDate'),
                        key: 'processed_at',
                        renderer: data => toFriendlyDateString(data) || '-',
                      },
                    ]}
                    onRowClick={data => this.openEditDrawer(data)}
                  />
                </MediaQuery>
              </StyledTableContainer>
            </Spacer>
          </Col>
        </Row>
        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.createBatchPaymentDrawerOpen}
          onClose={this.closeCreateBatchDrawer}
          closable={false}
          width={600}
        >
          {this.state.createBatchPaymentDrawerOpen && (
            <BatchPaymentCreating
              handleClose={this.closeCreateBatchDrawer}
              onSubmissionComplete={() => {
                this.props.getBatch(this.props.batch.id);
                this.reloadTable();
              }}
            />
          )}
        </AntDrawer>
        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.editBatchPaymentDrawerOpen}
          onClose={this.closeEditDrawer}
          closable={false}
          width={600}
        >
          {this.state.editBatchPaymentDrawerOpen && (
            <BatchPaymentEditing
              batchId={this.props.batch.id}
              batchPaymentId={this.state.batchPaymentId}
              handleClose={this.closeEditDrawer}
              handleDelete={this.openDeleteBatchPaymentDrawer}
              onSubmissionComplete={() => {
                this.props.getBatch(this.props.batch.id);
                this.reloadTable();
              }}
              formData={this.state.batchPayment}
              disabled={this.props.batch.status !== 'draft'}
            />
          )}
        </AntDrawer>
        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.deleteBatchPaymentDrawerOpen}
          onClose={this.closeDeleteBatchDrawer}
          closable={false}
          width={600}
        >
          {this.state.deleteBatchPaymentDrawerOpen && (
            <BatchPaymentDeleting
              handleClose={this.closeDeleteBatchPaymentDrawer}
              onSubmissionComplete={() => {
                this.props.getBatch(this.props.batch.id);
                this.reloadTable();
              }}
              batchId={this.props.batch.id}
              batchPaymentId={this.state.batchPaymentId}
              closeDeleteDialog={this.closeDeleteDialog}
            />
          )}
        </AntDrawer>
      </ScrollablePanel>
    );
  }
}

const mapStateToProps = store => ({
  batchPayments: store.landlord.batches.batchPayments,
  batchPaymentStats: store.landlord.batches.batchPaymentStats,
});

const actionCreators = {
  getFilteredBatchPayments,
  clearResidentsWithPAPDetails,
  getBatch,
  clearBatchPayments,
};

export default connect(mapStateToProps, actionCreators)(LinkedPayments);
